<script lang="ts">
  import { onMount } from "svelte";
  import type { Api, PageSnapshot } from "../lib/api";
  import {
    Icon,
    Button,
    Input,
    Checkbox,
    Select,
    SelectTrigger,
    SelectContent,
    SelectItem,
  } from "@price-check/ui";
  import { RefreshCw } from "lucide";
  import Card from "../lib/ui/Card.svelte";
  import Banner from "../lib/ui/Banner.svelte";
  import Spinner from "../lib/ui/Spinner.svelte";
  import EmptyState from "../lib/ui/EmptyState.svelte";
  import SnapshotViewer from "./SnapshotViewer.svelte";
  import SupplierSnapshotCard from "./snapshots/SupplierSnapshotCard.svelte";
  import VendorSnapshotList from "./snapshots/VendorSnapshotList.svelte";
  import {
    SNAPSHOT_KINDS,
    kindLabel,
    groupSnapshots,
    knownVendors,
    filterGroups,
    type KnownVendor,
    type SnapshotKind,
  } from "../lib/snapshot-groups";
  import { seg, navigate } from "../lib/route.svelte";

  let { api }: { api: Api } = $props();

  let snapshots = $state<PageSnapshot[]>([]);
  let vendors = $state<KnownVendor[]>([]);
  let loading = $state(true);
  let error = $state("");
  let kindFilter = $state<SnapshotKind | "">("");
  let missingOnly = $state(false);
  let query = $state("");

  // /snapshots/vendor/<vendorId>[/<snapshotId>] drills into ONE supplier's captures;
  // /snapshots/<snapshotId> opens the viewer straight from the grouped grid.
  const browsingVendor = $derived(seg(1) === "vendor" ? seg(2) : null);
  const viewingId = $derived(browsingVendor ? seg(3) : seg(1));

  const groups = $derived(groupSnapshots(snapshots, vendors));
  const visible = $derived(
    filterGroups(groups, { kind: kindFilter, missingOnly, query }),
  );
  const browsingName = $derived(
    groups.find((g) => g.vendorId === browsingVendor)?.vendorName,
  );

  async function load() {
    loading = true;
    error = "";
    try {
      const [snaps, defs] = await Promise.all([
        api.listSnapshots({ limit: 500 }),
        api.orgConfigDefs(),
      ]);
      snapshots = snaps;
      vendors = knownVendors(defs);
    } catch (e) {
      error = e instanceof Error ? e.message : "failed to load";
    } finally {
      loading = false;
    }
  }

  onMount(load);
</script>

{#if browsingVendor}
  <VendorSnapshotList
    {api}
    vendorId={browsingVendor}
    vendorName={browsingName}
    onBack={() => navigate("/snapshots")}
    onView={(id) => navigate(`/snapshots/vendor/${browsingVendor}/${id}`)}
  />
{:else}
<Card pad={false}>
  <header class="flex flex-wrap items-start gap-3 border-b border-border px-4.5 py-4">
    <div>
      <h3 class="m-0 text-md font-bold">Page snapshots</h3>
      <p class="mt-0.75 mb-0 text-sm text-muted">
        Captured supplier pages, grouped by supplier — the gaps show what still needs a capture.
      </p>
    </div>
    <div class="ml-auto flex flex-wrap items-center gap-2.5">
      <Select
        type="single"
        value={kindFilter || "all"}
        onValueChange={(v) => (kindFilter = v === "all" || !v ? "" : (v as SnapshotKind))}
      >
        <SelectTrigger size="sm" class="w-44" aria-label="Filter by page kind">
          {kindFilter ? kindLabel(kindFilter) : "All kinds"}
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all" label="All kinds" />
          {#each SNAPSHOT_KINDS as k (k)}
            <SelectItem value={k} label={kindLabel(k)} />
          {/each}
        </SelectContent>
      </Select>
      <label class="flex cursor-pointer items-center gap-1.75 text-sm font-semibold text-ink">
        <Checkbox bind:checked={missingOnly} aria-label="Missing only" />
        {kindFilter ? `Missing ${kindLabel(kindFilter)}` : "Missing something"}
      </label>
      <Input class="w-40" placeholder="Filter suppliers" bind:value={query} aria-label="Filter suppliers" />
      <Button variant="outline" size="sm" onclick={() => void load()} title="Refresh">
        <Icon icon={RefreshCw} size={14} /> Refresh
      </Button>
    </div>
  </header>

  {#if error}<div class="px-4.5 pt-3"><Banner tone="danger">{error}</Banner></div>{/if}

  {#if loading}
    <div class="px-4.5"><Spinner label="Loading snapshots…" /></div>
  {:else if groups.length === 0}
    <EmptyState
      icon="▢"
      title="No snapshots yet"
      hint="Captured pages will appear here as users run lookups."
    />
  {:else if visible.length === 0}
    <EmptyState icon="⌕" title="No suppliers match" hint="Loosen the filters above." />
  {:else}
    <div class="p-4.5">
      <p class="m-0 mb-3 text-xs text-muted">
        {visible.length} of {groups.length} suppliers
      </p>
      <ul class="m-0 grid list-none grid-cols-1 gap-3 p-0 min-[880px]:grid-cols-2">
        {#each visible as group (group.vendorId)}
          <SupplierSnapshotCard
            {group}
            onView={(id) => navigate(`/snapshots/${id}`)}
            onBrowse={(vendorId) => navigate(`/snapshots/vendor/${vendorId}`)}
          />
        {/each}
      </ul>
    </div>
  {/if}
</Card>
{/if}

{#if viewingId}
  <SnapshotViewer
    {api}
    id={viewingId}
    onClose={() =>
      navigate(browsingVendor ? `/snapshots/vendor/${browsingVendor}` : "/snapshots")}
  />
{/if}
